import { mediaMat } from "./imageBackgrounds";
import type { MediaResizeChange } from "./mediaSize";
import { MediaResize } from "./MediaResize";
import { Button, buttonClass } from "../../../components/ui";
import { FreeformCrop } from "./FreeformCrop";
import { PictureImage } from "./PictureImage";
import { captionAlignClass } from "./captionAlign";
import { useState, type ReactNode } from "react";
import { parseBlockContent, pictureStyle } from "../../../lib/blockContentSchemas";
import type { BlockContent } from "../../../lib/blockContentSchemas";
import type { BlockEditPatch, EditorAsset, EditorBlock } from "../editor.types";
import { EditActions, EditField } from "./BlockEditControls";
import { editInputClass, onEscapeCancel } from "../editUtils";


type PictureBlockProps = {
  block: EditorBlock;
  assetsById: Map<string, EditorAsset>;
  editing?: boolean;
  imageEditMode?: "crop" | "details";
  onSave?: (patch: BlockEditPatch) => Promise<string | null>;
  onCancelEdit?: () => void;
  /** In the editor: resizing by the bars at its sides. */
  onResize?: (change: MediaResizeChange) => void;
};

export const PictureBlock = ({
  block,
  assetsById,
  editing,
  imageEditMode = "crop",
  onSave,
  onCancelEdit,
  onResize,
}: PictureBlockProps): ReactNode => {
  const content = parseBlockContent("picture", block.content);
  const asset = block.asset_id ? assetsById.get(block.asset_id) : undefined;

  if (!asset && !content.sample) return null;

  const style = pictureStyle(content);
  const image = (
    <PictureImage content={content} asset={asset} square={style === "bleed"} />
  );

  if (editing) {
    return (
      <PictureBlockEditor
        mode={imageEditMode}
        content={content}
        asset={asset}
        onCancel={onCancelEdit}
        onSave={onSave}
      />
    );
  }

  const box = (child: ReactNode) => (
    <MediaResize size={content.size} width={content.width} onResize={onResize}>{child}</MediaResize>
  );
  const mat = mediaMat(content.background);
  // A picture narrower than the page keeps its caption under it, centred
  // on it unless the caption has an alignment of its own.
  const narrow = style === "standard" && (Boolean(content.width) || content.size !== "full");
  const caption = content.caption ? (
    <figcaption className={`mt-2.5 text-sm text-site-muted ${captionAlignClass(content.align ?? (narrow ? "center" : undefined))}`}>
      {content.caption}
    </figcaption>
  ) : null;
  return (
    <figure>
      {style === "framed" ? (
        <div className={`rounded-site px-6 py-8 sm:px-10 sm:py-12 ${mat}`}>
          {box(image)}
        </div>
      ) : style === "bleed" ? (
        // Spans its container (the preview frame, or the page) edge to edge;
        // inside a column it keeps to the column.
        <div className="mx-[calc(50%-50cqw)] [.site-column_&]:mx-0">{image}</div>
      ) : (
        box(narrow ? <>{image}{caption}</> : image)
      )}
      {!narrow && caption}
    </figure>
  );
};

type PictureBlockEditorProps = {
  mode: "crop" | "details";
  content: BlockContent<"picture">;
  asset?: EditorAsset;
  onSave?: (patch: BlockEditPatch) => Promise<string | null>;
  onCancel?: () => void;
};

const PictureBlockEditor = ({
  mode,
  content,
  asset,
  onSave,
  onCancel,
}: PictureBlockEditorProps) => {
  const [draftAlt, setDraftAlt] = useState(content.alt);
  const [draftCaption, setDraftCaption] = useState(content.caption);
  const [crop, setCrop] = useState<BlockContent<"picture">["crop"]>(content.crop ?? (mode === "crop" ? {ratio: "free", x: 50, y: 50, zoom: 1} : undefined));
  const [draftFit, setDraftFit] = useState(content.fit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const save = async () => {
    setSaving(true);
    setError("");
    const {crop: _previousCrop, ...base} = content;
    const result = await onSave?.({
      content: { ...base, alt: draftAlt, caption: draftCaption, fit: draftFit, ...(crop ? {crop} : {}) },
    });
    setSaving(false);
    if (result) setError(result);
  };

  return (
    <figure className="picture-block-editor space-y-3" onKeyDown={onEscapeCancel(onCancel)}>
      {mode === "crop" && <>
      {crop ? <FreeformCrop content={{...content, crop}} asset={asset} onChange={setCrop} /> : <PictureImage content={{...content, crop}} asset={asset} />}
      <fieldset className="space-y-3" disabled={saving}>
        <legend className="mb-2 text-sm font-semibold text-hazumu-ink">Crop image</legend>
        <div className="flex flex-wrap gap-2">
          {([["", "Original"], ["free", "Freeform"], ["1:1", "Square"], ["4:5", "Portrait"], ["3:2", "Landscape"], ["16:9", "Widescreen"]] as const).map(([ratio, label]) => <button type="button" key={label} aria-pressed={(crop?.ratio ?? '') === ratio} className={buttonClass({ size: "sm" }) + " aria-pressed:border-hazumu-terracotta aria-pressed:bg-hazumu-blush"} onClick={() => setCrop(ratio === "free" ? {ratio, x: 50, y: 50, zoom: 1} : ratio ? {ratio, x: 50, y: 50, zoom: 1} : undefined)}>{label}{ratio && ratio !== "free" && <span className="ml-1 text-xs">{ratio}</span>}</button>)}
        </div>
        {crop && <>
          <p className="text-sm text-hazumu-muted">{crop.ratio === "free" ? "Drag any corner or edge to crop. Drag inside the selection to move it." : "Drag the handles to resize. The selected ratio stays locked. Drag inside to move."}</p>
          <Button variant="ghost" size="sm" onClick={() => setCrop(undefined)}>Reset crop</Button>
        </>}
      </fieldset>
      </>}
      {mode === "details" && <>
      <EditField label="Alt text">
        <input
          className={editInputClass}
          onChange={(event) => setDraftAlt(event.target.value)}
          placeholder="Describe the image"
          value={draftAlt}
        />
      </EditField>
      <EditField label="Caption">
        <input
          className={editInputClass}
          onChange={(event) => setDraftCaption(event.target.value)}
          placeholder="Shown under the image"
          value={draftCaption}
        />
      </EditField>
      </>}
      {mode === "crop" && !crop && <EditField label="Fit">
        <select
          className={editInputClass}
          onChange={(event) =>
            setDraftFit(event.target.value as BlockContent<"picture">["fit"])
          }
          value={draftFit}
        >
          <option value="cover">Cover</option>
          <option value="contain">Contain</option>
          <option value="fill">Fill</option>
        </select>
      </EditField>}
      <EditActions
        error={error}
        onCancel={onCancel}
        onSave={save}
        saving={saving}
      />
    </figure>
  );
};
